import type { ScheduleMap } from '../models/plan-version-data.model';
import type { ShiftSkillRequirement } from '../models/shift-rules.model';

/**
 * Types of scheduling conflicts that can be detected.
 * Extensible union — add new conflict types as needed.
 *
 * Calendar conflicts: holiday, closed_day, absence
 * Shift overlap conflicts: duplicate, overlap, overnight_overlap
 * Skill conflicts: skill_expired
 */
/**
 * Types of scheduling conflicts that can be detected.
 * Extensible union — add new conflict types as needed.
 *
 * Calendar conflicts: holiday, closed_day, absence
 * Shift overlap conflicts: duplicate, overlap, overnight_overlap
 * Skill conflicts: skill_expired
 * Cross-team conflicts: cross_team_double_booking (floater assigned in both teams on same day)
 * Rule conflicts: rule_max_daily_hours, rule_max_consecutive_workdays, rule_min_rest_between_shifts
 */
export type ConflictType =
  | 'holiday'
  | 'closed_day'
  | 'absence'
  | 'duplicate'
  | 'overlap'
  | 'overnight_overlap'
  | 'skill_expired'
  | 'skill_missing'
  | 'skill_rating_below_minimum'
  | 'employment_inactive'
  | 'cross_team_double_booking'
  | 'shift_invalid_weekday'
  | 'rule_max_daily_hours'
  | 'rule_max_consecutive_workdays'
  | 'rule_min_rest_between_shifts';

/**
 * Maps each ConflictType to its i18n translation key.
 * Single source of truth — all UI components should import this
 * instead of maintaining their own switch statements.
 */
export const CONFLICT_TYPE_TRANSLATION_KEYS: Record<ConflictType, string> = {
  holiday: 'planning.dropErrors.holiday',
  closed_day: 'planning.dropErrors.closedDay',
  absence: 'planning.dropErrors.absence',
  duplicate: 'planning.dropErrors.duplicate',
  overlap: 'planning.dropErrors.shiftOverlap',
  overnight_overlap: 'planning.dropErrors.employeeOverlap',
  skill_expired: 'planning.dropErrors.skillExpired',
  // Shared with the drag & drop rejection copy — same user-facing meaning, so
  // reusing the keys keeps the grid and the conflict list saying one thing.
  skill_missing: 'planning.dropErrors.skillMismatch',
  skill_rating_below_minimum: 'planning.dropErrors.skillRatingBelowMinimum',
  employment_inactive: 'planning.dropErrors.employmentInactive',
  cross_team_double_booking: 'planning.dropErrors.crossTeamDoubleBooking',
  shift_invalid_weekday: 'planning.dropErrors.shiftInvalidWeekday',
  rule_max_daily_hours: 'planning.dropErrors.ruleMaxDailyHours',
  rule_max_consecutive_workdays: 'planning.dropErrors.ruleMaxConsecutiveWorkdays',
  rule_min_rest_between_shifts: 'planning.dropErrors.ruleMinRestBetweenShifts',
} as const;

/** Look up the translation key for a conflict type. Falls back to the overlap key for unknown types. */
export function getConflictTranslationKey(type: ConflictType): string {
  return CONFLICT_TYPE_TRANSLATION_KEYS[type] ?? CONFLICT_TYPE_TRANSLATION_KEYS.overlap;
}

/** Calendar-only conflict types (detected via calendar events). */
export type CalendarConflictType = 'holiday' | 'closed_day' | 'absence';

/** Shift overlap conflict types (detected via schedule analysis). */
export type ShiftOverlapConflictType = 'duplicate' | 'overlap' | 'overnight_overlap';

/** Skill qualification conflict types (expired, missing, or below minimum rating). */
export type SkillConflictType = 'skill_expired' | 'skill_missing' | 'skill_rating_below_minimum';

/** Type guard: returns true if the conflict type is a calendar conflict. */
export function isCalendarConflictType(type: ConflictType): type is CalendarConflictType {
  return type === 'holiday' || type === 'closed_day' || type === 'absence';
}

/** Type guard: returns true if the conflict type is a shift overlap conflict. */
export function isShiftOverlapConflictType(type: ConflictType): type is ShiftOverlapConflictType {
  return type === 'duplicate' || type === 'overlap' || type === 'overnight_overlap';
}

/** Type guard for all skill qualification conflict types. */
export function isSkillConflictType(type: ConflictType): type is SkillConflictType {
  return type === 'skill_expired' || type === 'skill_missing' || type === 'skill_rating_below_minimum';
}

/** Type guard: returns true if the conflict is a cross-team double booking. */
export function isCrossTeamConflictType(type: ConflictType): type is 'cross_team_double_booking' {
  return type === 'cross_team_double_booking';
}

/** Type guard: returns true if the conflict is an invalid-weekday assignment. */
export function isShiftWeekdayConflict(type: ConflictType): type is 'shift_invalid_weekday' {
  return type === 'shift_invalid_weekday';
}

export function conflictAffectsDateRange(conflict: ConflictResult, startDate: string, endDate: string): boolean {
  return (
    (conflict.date >= startDate && conflict.date <= endDate) ||
    (conflict.type.startsWith('rule_') &&
      (conflict.affectedAssignments?.some(assignment => assignment.date >= startDate && assignment.date <= endDate) ?? false))
  );
}

/** Timed absence window within a single day. Times are "HH:mm" wall-clock. */
export interface AbsenceWindow {
  startTime: string | null;
  endTime: string | null;
  isAllDay: boolean;
}

/**
 * A calendar event relevant to conflict detection.
 * Each event carries the set of person IDs it applies to (based on location linkage).
 *
 * @example
 * {
 *   date: '2026-01-01',
 *   type: 'holiday',
 *   label: 'Neujahr',
 *   calendarId: 'cal-uuid-123',
 *   applicablePersonIds: ['person-uuid-1', 'person-uuid-2'],
 * }
 */
export interface CalendarEvent {
  /** Date in YYYY-MM-DD format (date-only, no timezone) */
  date: string;
  type: ConflictType;
  label: string;
  calendarId: string;
  /** Person IDs this event applies to (based on their location linkage) */
  applicablePersonIds: string[];
  /** Holiday events only: the holiday's absence type key. */
  absenceTypeKey?: string;
  /** Timed absence window. Absent (or all-day/unpositioned) means full-day. */
  absenceWindow?: AbsenceWindow;
}

/**
 * A single conflict between a scheduled person and a calendar event or shift overlap.
 *
 * For calendar conflicts (holiday, closed_day, absence):
 *   - `calendarId` is the source calendar ID
 *   - `eventLabel` is the calendar event name (e.g. "Neujahr")
 *   - `shiftUnitId` and `conflictingShiftUnitId` are not set
 *
 * For shift overlap conflicts (duplicate, overlap, overnight_overlap):
 *   - `calendarId` is empty string (no calendar source)
 *   - `eventLabel` describes the overlap (e.g. "Shift overlap")
 *   - `shiftUnitId` is the specific shift unit in conflict
 *   - `conflictingShiftUnitId` is the other shift unit it overlaps with
 */
export interface ConflictResult {
  type: ConflictType;
  /** Date in YYYY-MM-DD format */
  date: string;
  personId: string;
  // Originating assignments for personId; their start dates may differ from the conflict evaluation date.
  affectedAssignments?: { date: string; shiftUnitId: string }[];
  eventLabel: string;
  calendarId: string;
  /** The specific shift unit involved in an overlap conflict */
  shiftUnitId?: string;
  /** The other shift unit that this one overlaps with */
  conflictingShiftUnitId?: string;
  /**
   * For cross_team_double_booking conflicts: the conflicting plan instance ID
   * in the other team where the floater is also assigned.
   */
  conflictingPlanInstanceId?: string;
  /**
   * For cross_team_double_booking conflicts: the name of the conflicting team.
   */
  conflictingTeamName?: string;
}

/**
 * Summary of conflicts for display purposes.
 * Computed by `summarizeConflicts()` from a flat `ConflictResult[]` array.
 */
export interface ConflictSummary {
  /** Count of unique personId values after deduplication */
  affectedPersonCount: number;
  /** Total number of conflicts in the summary bucket */
  totalConflictCount: number;
  /** Number of calendar conflicts (holiday, closed_day, absence) */
  calendarConflictCount: number;
  /** Number of overlap conflicts (duplicate, overlap, overnight_overlap) */
  overlapConflictCount: number;
  /** Number of skill qualification conflicts (expired, missing, or below minimum rating) */
  skillConflictCount: number;
  /** Number of cross-team double-booking conflicts (floater assigned in two teams on same day) */
  crossTeamConflictCount: number;
  /** Unique event labels, sorted alphabetically */
  eventLabels: string[];
}

/**
 * Conflict summary for a specific week, combining raw conflicts with computed summary.
 * Assembled by the caller (e.g., frontend `getConflictsForWeek`) by spreading
 * `summarizeConflicts(conflicts)` with `{ weekStart, conflicts }`.
 */
export interface WeekConflictSummary extends ConflictSummary {
  /** Week start date in YYYY-MM-DD format (matches WeekInfo.isoString) */
  weekStart: string;
  /** Raw conflict results for this week */
  conflicts: ConflictResult[];
}

export interface PersonSkillValidityRow {
  skillId: string;
  acquiredDate: string | null;
  expiryDate: string | null;
  rrule: string | null;
  rating: number | null;
}

export interface ConflictRuleContext {
  shiftUnits?: Map<string, { startTime: string; endTime: string; allowOverlap: boolean }>;
  personSkills?: Map<string, PersonSkillValidityRow[]>;
  shiftRequiredSkills?: Map<string, string[]>;
  shiftSkillRequirements?: Map<string, ShiftSkillRequirement[]>;
  skillNames?: Map<string, string>;
  shiftWeekdays?: Map<string, number[]>;
}

export interface ConflictRule {
  type: ConflictType;
  detect(schedule: ScheduleMap, events: CalendarEvent[], context?: ConflictRuleContext): ConflictResult[];
}
